<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus planos de pagamento
        </PageTitle>
        <PageDescription>
          Defina valores, prazos e disponibilize ofertas exclusivas aos seus clientes.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewPlanDialog">Cadastrar plano</RegisterButton>
    </div>

    <!-- Plans Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between flex-wrap gap-4">
        <h2 class="text-xl font-semibold text-white">Planos de pagamento ativos</h2>
        
        <div class="flex flex-wrap items-center gap-4">
          <div class="relative">
            <Input
              v-model="searchQuery"
              placeholder="Buscar planos..."
              class="w-[280px]"
              @keydown.enter="reloadAndGetAll({ type: typeFilter === 'all' ? undefined : typeFilter as PaymentPlanTypeEnum, search: searchQuery, withGroupsAndPackages: true })"
            >
              <template #prefix>
                <MagnifyingGlassIcon class="h-4 w-4 text-zinc-400" />
              </template>
            </Input>
          </div>
          
          <Select v-model="typeFilter">
            <SelectTrigger class="w-[180px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem v-for="type in PaymentPlanTypeEnum" :value="type">{{ PaymentPlanTypeLabels[type] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-zinc-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="paymentPlans">
            <TableFallback v-if="paymentPlans.data.length === 0" :colspan="tableHeaders.length" :icon="CreditCardIcon" title="Nenhum plano de pagamento encontrado" :description="`Clique em 'Cadastrar plano' para começar`" />
            <TableRow v-for="plan in paymentPlans.data" :key="plan.id">
              <TableCell class="whitespace-nowrap text-sm text-zinc-300">
                <div 
                  class="p-2 rounded inline-flex py-2.5 px-8 text-center text-wrap"
                  :style="{ 
                    backgroundColor: plan.buttonStyle === 'primary' ? '#2B6B84' : 
                                    plan.buttonStyle === 'success' ? '#377531' : 
                                    plan.buttonStyle === 'danger' ? '#A44236' : '#2c3b4b4f' 
                  }"
                >
                  {{ plan.buttonMessage }}
                </div>
              </TableCell>
              <TableCell>
                {{ plan.amount.toLocaleString('pt-BR', { currency: 'brl', style: 'currency' }) }} <br>
                <span class="text-xs text-zinc-300">{{ PaymentPlanTypeLabels[plan.type] }}</span>
              </TableCell>
              <TableCell class="whitespace-nowrap text-sm text-zinc-300">
                <div class="">
                  <div class="flex items-center gap-2 flex-wrap">
                    <Badge class="text-wrap" v-for="group in plan.groups">
                      {{ group.name }}
                    </Badge>
                    <Badge class="text-wrap" v-for="pkg in plan.packages" :key="pkg.id">
                      {{ pkg.name }}
                    </Badge>
                    <Badge class="text-wrap" v-for="vc in plan.videoCalls" :key="vc.id">
                      {{ vc.name }}
                    </Badge>
                  </div>
                  <div class="text-xs text-red-400 flex items-center gap-2" v-if="!plan.groups?.length && !plan.packages?.length && !plan.videoCalls?.length">
                    <Info class="size-4 text-red-400" />
                    Vincule um grupo, pack ou vídeo chamada
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-1.5 flex-wrap max-w-[200px]">
                  <template v-if="plan.downsells && plan.downsells.length > 0">
                    <Badge v-for="ds in plan.downsells" :key="ds.id" variant="outline" class="text-[10px] text-white py-0 bg-yellow-700">
                      {{ ds.description }}
                    </Badge>
                  </template>
                  <span v-else class="text-[10px] text-zinc-500 italic">Nenhum</span>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge :status="plan.status" />
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editPlan(plan)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deletePlan(plan)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton :columns="tableHeaders.length" v-else />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="paymentPlans">
        <template v-if="paymentPlans.data.length > 0">
          <MobileCard v-for="plan in paymentPlans.data" :key="plan.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Plano</span>
                  <div class="flex items-center gap-2">
                    <span class="text-white font-bold text-lg">{{ plan.name }}</span>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editPlan(plan)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="deletePlan(plan)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <div class="grid grid-cols-2 gap-4">
                   <MobileDataLabel label="Valor">
                      {{ plan.amount.toLocaleString('pt-BR', { currency: 'brl', style: 'currency' }) }}
                      <span class="text-xs text-zinc-500 block">{{ PaymentPlanTypeLabels[plan.type] }}</span>
                   </MobileDataLabel>
                   
                   <MobileDataLabel label="Botão">
                      <div 
                        class="p-1 px-3 rounded inline-flex text-center text-xs truncate max-w-full"
                        :style="{ 
                          backgroundColor: plan.buttonStyle === 'primary' ? '#2B6B84' : 
                                          plan.buttonStyle === 'success' ? '#377531' : 
                                          plan.buttonStyle === 'danger' ? '#A44236' : '#2c3b4b4f' 
                        }"
                      >
                        {{ plan.buttonMessage }}
                      </div>
                   </MobileDataLabel>
               </div>

               <MobileDataLabel label="Vínculos">
                   <div class="flex flex-wrap gap-1" v-if="plan.groups?.length || plan.packages?.length">
                      <Badge v-for="group in plan.groups" :key="group.id" class="text-[10px]">
                        {{ group.name }}
                      </Badge>
                      <Badge v-for="pkg in plan.packages" :key="pkg.id" class="text-[10px]">
                        {{ pkg.name }}
                      </Badge>
                      <Badge v-for="vc in plan.videoCalls" :key="vc.id" class="text-[10px]">
                        {{ vc.name }}
                      </Badge>
                   </div>
                   <div class="text-xs text-red-400 flex items-center gap-1 mt-1" v-if="!plan.groups?.length && !plan.packages?.length && !plan.videoCalls?.length">
                      <Info class="size-3 text-red-400" />
                      Vincule um grupo, pack ou vídeo chamada
                   </div>
               </MobileDataLabel>
               
               <MobileDataLabel label="Downsells" v-if="plan.downsells && plan.downsells.length > 0">
                  <div class="flex flex-wrap gap-1">
                      <Badge v-for="ds in plan.downsells" :key="ds.id" variant="outline" class="text-[10px] text-white py-0 bg-yellow-700">
                        {{ ds.description }}
                      </Badge>
                  </div>
               </MobileDataLabel>

               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <StatusBadge :status="plan.status" />
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <CreditCardIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum plano de pagamento encontrado
        </div>
      </div>

      <!-- Pagination -->
      <Pagination @update:page="page => reloadAndGetAll({ page, withGroupsAndPackages: true })" v-if="paymentPlans?.pagination" :data="paymentPlans?.pagination" v-model:per-page="perPage" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeletePlanDialogOpen" :loading="isDeletingPlan" @confirm="isDeletingPlan = true; destroy(deletingPLan!, () => {
      toast({
        title: 'Plano de Pagamento excluído com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      isDeletingPlan = false
      isDeletePlanDialogOpen = false
      const index = paymentPlans?.data.findIndex(plan => plan.id == deletingPLan)
      if (index !== -1) paymentPlans?.data.splice(index!, 1)
    })" />

  <!-- New/Edit Plan Dialog -->
  <Dialog v-model:open="isPlanDialogOpen">
    <DialogScrollContent class="sm:max-w-3xl">
      <DialogHeader>
        <DialogTitle>{{ editingPlan ? 'Editar plano' : 'Novo plano' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do plano de pagamento.
        </DialogDescription>
      </DialogHeader>
      <Tabs v-model="planTab" v-if="!isPlanDialogLoading" defaultValue="general" class="w-full">
        <TabsList class="grid w-full grid-cols-2 lg:grid-cols-6 mb-4 bg-zinc-800 text-white">
          <TabsTrigger value="general">Geral</TabsTrigger>
          <TabsTrigger value="checkout">
            <div class="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 25 24" fill="none">
                <path d="M2.03027 8.505H22.0303" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.03027 16.505H8.03027" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.5303 16.505H14.5303" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M6.47027 3.505H17.5803C21.1403 3.505 22.0303 4.385 22.0303 7.895V16.105C22.0303 19.615 21.1403 20.495 17.5903 20.495H6.47027C2.92027 20.505 2.03027 19.625 2.03027 16.115V7.895C2.03027 4.385 2.92027 3.505 6.47027 3.505Z" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Checkout
            </div>
          </TabsTrigger>
          <TabsTrigger value="upsell">
            <div class="flex items-center gap-1">
              <ArrowUp />
              Upsell
            </div>
          </TabsTrigger>
          <template v-if="!editingPlan">
            <TooltipProvider>
              <Tooltip :delay-duration="0">
                <TooltipTrigger as-child>
                  <span class="inline-block">
                    <TabsTrigger disabled value="downsell" class="opacity-50 cursor-not-allowed">
                      <div class="flex items-center gap-1">
                        <ArrowDown />
                        Downsell
                      </div>
                    </TabsTrigger>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Para configurar o downsell, primeiro crie o seu plano.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </template>
          <TabsTrigger v-else value="downsell">
            <div class="flex items-center gap-1">
              <ArrowDown />
              Downsell
            </div>
          </TabsTrigger>
          <TabsTrigger value="order_bumps">
            <div class="flex items-center gap-1">
              <ArrowUp />
              Order Bumps
            </div>
          </TabsTrigger>
          <TabsTrigger value="video_calls">
            <div class="flex items-center gap-1">
              <Phone class="size-4" />
              V. Chamada
            </div>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <div class="grid grid-cols-2 gap-6 py-4">
            <div class="grid gap-2 col-span-full">
              <Label>Nome do plano <span class="text-red-500">*</span></Label>
              <Input v-model="planForm.name" placeholder="Ex: Plano Premium" />
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Valor <span class="text-red-500">*</span></Label>
              <div class="relative">
                <Input 
                  v-model="planForm.amount" 
                  type="number" 
                  placeholder="0,00"
                  min="1"
                  step="0.01"
                />
                <DollarSign class="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9CA3AF]" />
              </div>
            </div>
            <div :class="{ 'col-span-full': planForm.type === PaymentPlanTypeEnum.LIFETIME, 'sm:col-span-1': planForm.type !== PaymentPlanTypeEnum.LIFETIME }" 
            class="grid gap-2 col-span-full">
              <Label>Tipo de cobrança <span class="text-red-500">*</span></Label>
              <Select v-model="planForm.type">
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="type in PaymentPlanTypeEnum" :value="type">{{ PaymentPlanTypeLabels[type] }}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div v-if="planForm.type !== PaymentPlanTypeEnum.LIFETIME" class="grid gap-2 col-span-full sm:col-span-1">
              <div class="flex justify-between items-center gap-2">
                <Label>Cobrança a cada <span class="text-red-500">*</span></Label>
                <span v-if="planForm.type" class="text-zinc-400 text-xs">{{ cycleLabel }}</span>
              </div>
              <Input 
                v-model="planForm.cycle" 
                type="number" 
                placeholder="Ex: 1"
                min="1"
                step="0.01"
              />
            </div>
            <div class="grid gap-2 col-span-full">
              <div class="flex items-center gap-2">
                <Label>Status <span class="text-red-500">*</span></Label>
                <TooltipProvider>
                  <Tooltip :delay-duration="100">
                    <TooltipTrigger as-child>
                      <CircleHelp class="size-5 text-primary" />
                    </TooltipTrigger>
                    <TooltipContent side="top" :side-offset="5">
                      Status oculto: Este plano não será exibido <br> quando o usuário iniciar o bot.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Select v-model="planForm.status">
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="status in PaymentPlanStatusEnum" :value="status">{{ PaymentPlanStatusLabels[status] }}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Mensagem do botão</Label>
              <Input v-model="planForm.buttonMessage" placeholder="Ex: Plano Premium" />
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Cor do botão</Label>
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  @click="planForm.buttonStyle = undefined"
                  class="size-8 rounded-full border-2 transition-all hover:scale-110"
                  :class="[planForm.buttonStyle === undefined || planForm.buttonStyle === '' ? 'border-white scale-110' : 'border-transparent']"
                  style="background-color: #2c3b4b4f"
                  title="Padrão (Cinza)"
                ></button>
                <button
                  type="button"
                  @click="planForm.buttonStyle = 'primary'"
                  class="size-8 rounded-full border-2 transition-all hover:scale-110"
                  :class="[planForm.buttonStyle === 'primary' ? 'border-white scale-110' : 'border-transparent']"
                  style="background-color: #2B6B84"
                  title="Azul"
                ></button>
                <button
                  type="button"
                  @click="planForm.buttonStyle = 'success'"
                  class="size-8 rounded-full border-2 transition-all hover:scale-110"
                  :class="[planForm.buttonStyle === 'success' ? 'border-white scale-110' : 'border-transparent']"
                  style="background-color: #377531"
                  title="Verde"
                ></button>
                <button
                  type="button"
                  @click="planForm.buttonStyle = 'danger'"
                  class="size-8 rounded-full border-2 transition-all hover:scale-110"
                  :class="[planForm.buttonStyle === 'danger' ? 'border-white scale-110' : 'border-transparent']"
                  style="background-color: #A44236"
                  title="Vermelho"
                ></button>
              </div>
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Mensagem de pagamento aprovado</Label>
              <Textarea v-model="planForm.paymentApprovedMessage" placeholder="Ex: ✅ Venda confirmada com sucesso!" />
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Grupos</Label>
              <TagsInput v-if="filteredGroups" class="px-0 gap-0 w-full" :model-value="groupNames">
                <div class="flex gap-2 flex-wrap items-center px-3">
                  <TagsInputItem v-for="item in groupNames" :key="item" :value="item">
                    <TagsInputItemText />
                    <TagsInputItemDelete @click="() => {
                      const group = groups?.find(p => p.name == item)
                      if (group) {
                        planForm.groupIds = planForm.groupIds?.filter(id => String(id) !== String(group.id))
                        groupNames = groupNames.filter(i => i !== item)
                      }
                    }" />
                  </TagsInputItem>
                </div>
                
                <ComboboxRoot v-model="groupNames" v-model:open="isGroupComboboxOpen" v-model:search-term="searchGroup" class="w-full">
                  <ComboboxAnchor as-child>
                    <ComboboxInput placeholder="Selecione os grupos..." as-child>
                      <TagsInputInput @focus="isGroupComboboxOpen = true" class="w-full px-3" :class="groupNames.length > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                    </ComboboxInput>
                  </ComboboxAnchor>

                  <ComboboxPortal>
                    <ComboboxContent>
                      <CommandList
                        position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                      >
                        <CommandEmpty>Nenhum grupo encontrado</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="group in filteredGroups" :key="group.id" :value="String(group.name)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchGroup = ''
                                const group = filteredGroups?.find(p => p.name == ev.detail.value)
                                planForm.groupIds?.push(Number(group!.id))
                                groupNames.push(group!.name)
                              }

                              if (filteredGroups?.length === 0) {
                                isGroupComboboxOpen = false
                              }
                            }"
                          >
                            {{ group.name }}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </ComboboxContent>
                  </ComboboxPortal>
                </ComboboxRoot>
              </TagsInput>
              <p class="text-xs text-gray-400">Você pode anexar um ou mais grupos a este plano</p>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="checkout">
          <div class="grid grid-cols-1 gap-6 py-4">
            <div>
              <div class="grid gap-3">
                <Label>Produto <span class="text-red-500">*</span></Label>
                <Select :model-value="String(planForm.productId)" @update:modelValue="(value) => changeProductId(Number(value))">
                  <SelectTrigger id="productId" aria-label="Selecionar o produto">
                    <SelectValue placeholder="Selecionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem v-for="product in products?.data" :key="product.id" :value="String(product.id)">{{ product.title }}</SelectItem>
                    <div 
                      v-if="!products" 
                      class="flex items-center justify-center p-4"
                    >
                      <Loader2 class="w-5 h-5 animate-spin" />
                    </div>
                    <div v-if="products && products.data.length === 0" class="flex items-center justify-center p-4">
                      <Button @click="createProduct" class="text-sm text-center mx-auto">
                        <PlusIcon class="h-5 w-5 hidden 2xl:block" />
                        Cadastrar produto
                      </Button>
                    </div>
                  </SelectContent>
                </Select>
              </div>
              <p v-if="!planForm.productId" class="text-xs text-yellow-400 pt-2 flex items-center gap-2">
                <Info class="size-4 text-yellow-400" />
                <p>Caso seu produto não esteja visível, é necessário ativar a integração Bot Express na HooPay para ele ser selecionável.</p>
              </p>
            </div>
            <div v-if="planForm.productId" class="grid gap-3">
              <Label>Variante <span class="text-red-500">*</span></Label>
              <Select v-model="planForm.variantUUID">
                <SelectTrigger id="variantId" aria-label="Selecionar variante">
                  <SelectValue placeholder="Selecionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="variant in variants" :key="variant.id" :value="String(variant.uuid)">
                    {{ variant.title }}
                  </SelectItem>
                  <div 
                    v-if="isVariantsLoading" 
                    class="flex items-center justify-center p-4"
                  >
                    <Loader2 class="w-5 h-5 animate-spin" />
                  </div>
                  <div v-if="!isVariantsLoading && variants && variants.length === 0" class="flex items-center justify-center p-4">
                    <Button @click="createVariant" class="text-sm text-center mx-auto">
                      <PlusIcon class="h-5 w-5 hidden 2xl:block" />
                      Cadastrar variante
                    </Button>
                  </div>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div class="grid gap-2 col-span-full">
                <Label>Mensagem do botão <span class="text-red-500">*</span></Label>
                <Input v-model="planForm.checkoutButtonLabel" placeholder="Ex: Cartão de Crédito ou Checkout" />
              </div>
              <p v-if="!planForm.variantUUID" class="text-xs text-yellow-400 pt-2 flex items-center gap-2">
                <Info class="size-4 text-yellow-400" />
                O método de pagamento com checkout só ficará disponível ao selecionar um variante.
              </p>
              <div class="flex items-center gap-2 pt-2">
                <HelpCircle class="h-4 w-4 text-emerald-400 flex-shrink-0" />
                <p class="text-xs text-zinc-300 mb-0 cursor-pointer" @click="$router.push({ name: 'help', query: { tutorialId: 3, step: 4 } })">Não entendeu? <span class="text-emerald-400">Clique aqui.</span></p>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="upsell">
          <div class="grid grid-cols-2 gap-6 py-4">
            <div class="grid gap-2 col-span-full">
              <Label>Mensagem de upsell <span class="text-red-500">*</span></Label>
              <RichTextarea 
                v-model="planForm.upsellMessage" 
                placeholder="Ex: Você pode gostar também..." 
                :variables="[
                  { label: 'Nome', value: '{{nome}}' }
                ]"
              />
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Planos</Label>
              <TagsInput v-if="filteredPaymentPlans" class="px-0 gap-0 w-full" :model-value="paymentPlanNames">
                <div class="flex gap-2 flex-wrap items-center px-3">
                  <TagsInputItem v-for="(name, index) in paymentPlanNames" :key="index" :value="name">
                    <TagsInputItemText />
                    <TagsInputItemDelete @click="planForm.upsellPaymentPlanIds?.splice(index, 1); paymentPlanNames.splice(index, 1)" />
                  </TagsInputItem>
                </div>
                
                <ComboboxRoot v-model="paymentPlanNames" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
                  <ComboboxAnchor as-child>
                    <ComboboxInput placeholder="Plano premium..." as-child>
                      <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(planForm.upsellPaymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                    </ComboboxInput>
                  </ComboboxAnchor>
            
                  <ComboboxPortal>
                    <ComboboxContent>
                      <CommandList
                        position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                      >
                        <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="paymentPlan in filteredPaymentPlans" :key="paymentPlan.id" :value="String(paymentPlan.name)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchPaymentPlan = ''
                                 const paymentPlan = filteredPaymentPlans?.find(plan => plan.name == ev.detail.value)
                                planForm.upsellPaymentPlanIds?.push(paymentPlan!.id)
                                paymentPlanNames.push(paymentPlan!.name)
                                upsell = true
                              }
            
                              if (filteredPaymentPlans?.length === 0) {
                                isPaymentPlanComboboxOpen = false
                              }
                            }"
                          >
                            {{ paymentPlan.name }}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </ComboboxContent>
                  </ComboboxPortal>
                </ComboboxRoot>
              </TagsInput>
              <p class="text-xs text-gray-400">Você pode selecionar mais de 1 plano</p>
            </div>
            <div class="grid gap-2 col-span-full">
              <Label>Links de redirecionamento</Label>
              <TagsInput v-if="filteredButtons" class="px-0 gap-0 w-full" :model-value="buttonNames">
                <div class="flex gap-2 flex-wrap items-center px-3">
                  <TagsInputItem v-for="(name, index) in buttonNames" :key="index" :value="name">
                    <TagsInputItemText />
                    <TagsInputItemDelete @click="planForm.upsellButtonIds?.splice(index, 1); buttonNames.splice(index, 1)" />
                  </TagsInputItem>
                </div>
                
                <ComboboxRoot v-model="buttonNames" v-model:open="isButtonComboboxOpen" v-model:search-term="searchButton" class="w-full">
                  <ComboboxAnchor as-child>
                    <ComboboxInput placeholder="Link para landing page..." as-child>
                      <TagsInputInput @focus="isButtonComboboxOpen = true" class="w-full px-3" :class="(planForm.upsellButtonIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                    </ComboboxInput>
                  </ComboboxAnchor>
            
                  <ComboboxPortal>
                    <ComboboxContent>
                      <CommandList
                        position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                      >
                        <CommandEmpty>Nenhum link encontrado</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="button in filteredButtons" :key="button.id" :value="String(button.name)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchButton = ''
                                 const button = filteredButtons?.find(plan => plan.name == ev.detail.value)
                                planForm.upsellButtonIds?.push(button!.id)
                                buttonNames.push(button!.name)
                                upsell = true
                              }
            
                              if (filteredButtons?.length === 0) {
                                isButtonComboboxOpen = false
                              }
                            }"
                          >
                            {{ button.name }}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </ComboboxContent>
                  </ComboboxPortal>
                </ComboboxRoot>
              </TagsInput>
              <p class="text-xs text-gray-400">Você pode selecionar mais de 1 link</p>
            </div>
            <div class="flex gap-6 col-span-full">
              <div class="flex items-center gap-2">
                <Switch v-model:checked="upsell" />
                <Label>{{ upsell ? 'Ativo' : 'Inativo' }}</Label>
              </div>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="order_bumps">
            <div class="grid gap-2 col-span-full py-4">
              <Label>Order Bumps Associados</Label>
              <TagsInput v-if="filteredOrderBumps" class="px-0 gap-0 w-full" :model-value="orderBumpNames">
                <div class="flex gap-2 flex-wrap items-center px-3">
                  <TagsInputItem v-for="(name, index) in orderBumpNames" :key="index" :value="name">
                    <TagsInputItemText />
                    <TagsInputItemDelete @click="planForm.orderBumpIds?.splice(index, 1); orderBumpNames.splice(index, 1)" />
                  </TagsInputItem>
                </div>
                
                <ComboboxRoot v-model="orderBumpNames" v-model:open="isOrderBumpComboboxOpen" v-model:search-term="searchOrderBump" class="w-full">
                  <ComboboxAnchor as-child>
                    <ComboboxInput placeholder="Selecionar order bump..." as-child>
                      <TagsInputInput @focus="isOrderBumpComboboxOpen = true" class="w-full px-3" :class="(planForm.orderBumpIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                    </ComboboxInput>
                  </ComboboxAnchor>
            
                  <ComboboxPortal>
                    <ComboboxContent>
                      <CommandList
                        position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                      >
                        <CommandEmpty>Nenhum order bump encontrado</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="bump in filteredOrderBumps" :key="bump.id" :value="String(bump.name)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchOrderBump = ''
                                const bump = filteredOrderBumps?.find(b => b.name == ev.detail.value)
                                planForm.orderBumpIds?.push(bump!.id)
                                orderBumpNames.push(bump!.name)
                              }
            
                              if (filteredOrderBumps?.length === 0) {
                                isOrderBumpComboboxOpen = false
                              }
                            }"
                          >
                            {{ bump.name }}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </ComboboxContent>
                  </ComboboxPortal>
                </ComboboxRoot>
              </TagsInput>
              <p class="text-xs text-gray-400">Ofertas exibidas ao selecionar este plano.</p>
            </div>
        </TabsContent>
        <TabsContent value="video_calls">
            <div class="grid gap-2 col-span-full py-4">
              <Label>Vídeo Chamadas Associadas</Label>
              <TagsInput v-if="filteredVideoCalls" class="px-0 gap-0 w-full" :model-value="videoCallNames">
                <div class="flex gap-2 flex-wrap items-center px-3">
                  <TagsInputItem v-for="(name, index) in videoCallNames" :key="index" :value="name">
                    <TagsInputItemText />
                    <TagsInputItemDelete @click="planForm.videoCallIds?.splice(index, 1); videoCallNames.splice(index, 1)" />
                  </TagsInputItem>
                </div>
                
                <ComboboxRoot v-model="videoCallNames" v-model:open="isVideoCallComboboxOpen" v-model:search-term="searchVideoCall" class="w-full">
                  <ComboboxAnchor as-child>
                    <ComboboxInput placeholder="Selecionar vídeo chamada..." as-child>
                      <TagsInputInput @focus="isVideoCallComboboxOpen = true" class="w-full px-3" :class="(planForm.videoCallIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                    </ComboboxInput>
                  </ComboboxAnchor>
            
                  <ComboboxPortal>
                    <ComboboxContent>
                      <CommandList
                        position="popper"
                        class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                      >
                        <CommandEmpty>Nenhuma vídeo chamada encontrada</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            v-for="vc in filteredVideoCalls" :key="vc.id" :value="String(vc.name)"
                            @select.prevent="(ev) => {
                              if (typeof ev.detail.value === 'string') {
                                searchVideoCall = ''
                                const vc = filteredVideoCalls?.find(v => v.name == ev.detail.value)
                                planForm.videoCallIds?.push(vc!.id)
                                videoCallNames.push(vc!.name)
                              }
            
                              if (filteredVideoCalls?.length === 0) {
                                isVideoCallComboboxOpen = false
                              }
                            }"
                          >
                            {{ vc.name }}
                          </CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </ComboboxContent>
                  </ComboboxPortal>
                </ComboboxRoot>
              </TagsInput>
              <p class="text-xs text-gray-400">Vídeos exibidos após o pagamento deste plano.</p>
            </div>
        </TabsContent>
        <TabsContent value="downsell">
          <div class="space-y-6 py-4">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-sm font-semibold text-white">Mensagens de Recuperação</h3>
                <p class="text-xs text-zinc-400 mt-1">Configure disparos automáticos após abandono de checkout.</p>
              </div>
              <Button v-if="!isAddingDownsell" size="sm" class="gap-2 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 hover:border-primary/50 transition-all" @click="openNewDownsellForm">
                <PlusIcon class="size-4" />
                Novo Downsell
              </Button>
            </div>

            <!-- Add/Edit Downsell Form (Matching OldManageMessagesView style) -->
            <div v-if="isAddingDownsell" class="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
              <div class="grid md:grid-cols-2 gap-6">
                <!-- Col 1 -->
                <div class="space-y-6">
                  <div class="flex flex-col gap-2">
                    <Label>Descrição <span class="text-red-500">*</span></Label>
                    <Input v-model="downsellForm.description" placeholder="Ex: Downsell 1 hora" />
                  </div>

                  <div class="flex flex-col gap-2">
                    <AnyFileUpload 
                      v-model:preview="downsellStartMediaPreview!" 
                      v-model="downsellStartMedia" 
                      v-model:has-spoiler="downsellForm.hasSpoiler"
                      @update:model-value="(f) => downsellStartMedia = f" 
                    />
                  </div>

                  <!-- Footer Image (Commented as requested) -->
                  <!--
                  <div class="flex flex-col gap-2">
                    <Label class="text-xs font-bold text-zinc-400 uppercase">Imagem de Rodapé (Opcional)</Label>
                    <AnyFileUpload v-model:preview="downsellStartMediaPreview" v-model="downsellStartMedia" @update:model-value="(f) => downsellStartMedia = f" />
                  </div>
                  -->

                  <div class="grid gap-2">
                    <Label>Mensagem</Label>
                    <RichTextarea 
                      v-model="downsellForm.message" 
                      placeholder="Olá {{nome}}, vimos que você quase finalizou..." 
                      :rows="4" 
                      :variables="[
                        { label: 'Nome', value: '{{nome}}' }
                      ]"
                    />
                  </div>
                </div>

                <!-- Col 2 -->
                <div class="space-y-6">
                  <div class="grid gap-2">
                    <Label>Planos</Label>
                    <TagsInput v-if="paymentPlans" class="px-0 gap-0 w-full" :model-value="paymentPlanNamesInDownsell">
                      <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in paymentPlanNamesInDownsell" :key="index" :value="name">
                          <TagsInputItemText />
                          <TagsInputItemDelete @click="downsellForm.paymentPlanIds?.splice(index, 1); paymentPlanNamesInDownsell.splice(index, 1)" />
                        </TagsInputItem>
                      </div>
                      <ComboboxRoot v-model="paymentPlanNamesInDownsell" v-model:open="isPaymentPlanComboboxOpen" v-model:search-term="searchPaymentPlan" class="w-full">
                        <ComboboxAnchor as-child>
                          <ComboboxInput placeholder="Plano premium..." as-child>
                            <TagsInputInput @focus="isPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(downsellForm.paymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                          </ComboboxInput>
                        </ComboboxAnchor>
                        <ComboboxPortal>
                          <ComboboxContent>
                            <CommandList
                              position="popper"
                              class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                            >
                              <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  v-for="plan in filteredPlansForDownsellBody" :key="plan.id" :value="String(plan.name)"
                                  @select.prevent="(ev) => {
                                    if (typeof ev.detail.value === 'string') {
                                      searchPaymentPlan = ''
                                      const p = paymentPlans?.data.find(pp => pp.name == ev.detail.value)
                                      if (p && !downsellForm.paymentPlanIds.includes(String(p.id))) {
                                        downsellForm.paymentPlanIds?.push(String(p.id))
                                        paymentPlanNamesInDownsell.push(p.name)
                                      }
                                    }
                                    
                                    if (filteredPlansForDownsellBody?.length === 0) {
                                      isPaymentPlanComboboxOpen = false
                                    }
                                  }"
                                >
                                  {{ plan.name }}
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </ComboboxContent>
                        </ComboboxPortal>
                      </ComboboxRoot>
                    </TagsInput>
                  </div>

                  <div class="grid gap-2">
                    <Label>Links</Label>
                    <TagsInput v-if="buttons" class="px-0 gap-0 w-full" :model-value="downsellButtonNames">
                      <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in downsellButtonNames" :key="index" :value="name">
                          <TagsInputItemText />
                          <TagsInputItemDelete @click="downsellForm.buttonIds?.splice(index, 1); downsellButtonNames.splice(index, 1)" />
                        </TagsInputItem>
                      </div>
                      <ComboboxRoot v-model="downsellButtonNames" v-model:open="isButtonComboboxOpen" v-model:search-term="searchButton" class="w-full">
                        <ComboboxAnchor as-child>
                          <ComboboxInput placeholder="Link para landing page..." as-child>
                            <TagsInputInput @focus="isButtonComboboxOpen = true" class="w-full px-3" :class="(downsellForm.buttonIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                          </ComboboxInput>
                        </ComboboxAnchor>
                        <ComboboxPortal>
                          <ComboboxContent>
                            <CommandList
                              position="popper"
                              class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                            >
                              <CommandEmpty>Nenhum link encontrado</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  v-for="btn in filteredDownsellButtons" :key="btn.id" :value="String(btn.name)"
                                  @select.prevent="(ev) => {
                                    if (typeof ev.detail.value === 'string') {
                                      searchButton = ''
                                      const b = buttons?.data.find(bt => bt.name == ev.detail.value)
                                      if (b && !downsellForm.buttonIds.includes(String(b.id))) {
                                        downsellForm.buttonIds?.push(String(b.id))
                                        downsellButtonNames.push(b.name)
                                      }
                                    }
                                    
                                    if (filteredDownsellButtons?.length === 0) {
                                      isButtonComboboxOpen = false
                                    }
                                  }"
                                >
                                  {{ btn.name }}
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </ComboboxContent>
                        </ComboboxPortal>
                      </ComboboxRoot>
                    </TagsInput>
                  </div>

                  <div class="grid gap-2">
                    <Label>Planos de pagamento abandonados <span class="text-red-500">*</span></Label>
                    <TagsInput v-if="paymentPlans" class="px-0 gap-0 w-full" :model-value="downsellPaymentPlanNames">
                      <div class="flex gap-2 flex-wrap items-center px-3">
                        <TagsInputItem v-for="(name, index) in downsellPaymentPlanNames" :key="index" :value="name">
                          <TagsInputItemText />
                          <TagsInputItemDelete v-if="String(editingPlan?.id) !== String(downsellForm.downsellPaymentPlanIds?.[index])" @click="downsellForm.downsellPaymentPlanIds?.splice(index, 1); downsellPaymentPlanNames.splice(index, 1)" />
                        </TagsInputItem>
                      </div>
                      <ComboboxRoot v-model="downsellPaymentPlanNames" v-model:open="isDownsellPaymentPlanComboboxOpen" v-model:search-term="searchDownsellPaymentPlan" class="w-full">
                        <ComboboxAnchor as-child>
                          <ComboboxInput placeholder="Plano premium..." as-child>
                            <TagsInputInput @focus="isDownsellPaymentPlanComboboxOpen = true" class="w-full px-3" :class="(downsellForm.downsellPaymentPlanIds?.length ?? 0) > 0 ? 'mt-2' : ''" @keydown.enter.prevent />
                          </ComboboxInput>
                        </ComboboxAnchor>
                        <ComboboxPortal>
                          <ComboboxContent>
                            <CommandList
                              position="popper"
                              class="w-[--radix-popper-anchor-width] rounded-md mt-2 border bg-popover text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50"
                            >
                              <CommandEmpty>Nenhum plano encontrado</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  v-for="plan in filteredDownsellPaymentPlans" :key="plan.id" :value="String(plan.name)"
                                  @select.prevent="(ev) => {
                                    if (typeof ev.detail.value === 'string') {
                                      searchDownsellPaymentPlan = ''
                                      const p = paymentPlans?.data.find(pp => pp.name == ev.detail.value)
                                      if (p && !downsellForm.downsellPaymentPlanIds.includes(String(p.id))) {
                                        downsellForm.downsellPaymentPlanIds?.push(String(p.id))
                                        downsellPaymentPlanNames.push(p.name)
                                      }
                                    }
                                    
                                    if (filteredDownsellPaymentPlans?.length === 0) {
                                      isDownsellPaymentPlanComboboxOpen = false
                                    }
                                  }"
                                >
                                  {{ plan.name }}
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </ComboboxContent>
                        </ComboboxPortal>
                      </ComboboxRoot>
                    </TagsInput>
                  </div>

                  <div class="grid gap-2">
                    <Label>Intervalo de envio após abandono <span class="text-red-500">*</span> (horas e minutos)</Label>
                    <TimePicker v-model="downsellIntervalTime" />
                  </div>

                  <div class="flex items-center gap-3">
                    <Switch v-model:checked="downsellForm.abandonedChat" />
                    <div class="grid gap-0.5">
                      <Label class="text-sm">Abandono de Carrinho (Chat)</Label>
                      <span class="text-[10px] text-zinc-500">Enviar mesmo para quem não gerou pix.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex items-center justify-end gap-3 pt-4 border-t border-zinc-800">
                <Button variant="ghost" size="sm" class="text-zinc-400 hover:text-white" @click="cancelDownsellEdit">Cancelar</Button>
                <Button size="sm" :disabled="isSavingDownsell || !downsellForm.description || !downsellForm.message" @click="saveDownsell">
                  <Loader2 v-if="isSavingDownsell" class="size-4 animate-spin mr-2" />
                  {{ editingDownsellId ? 'Salvar alterações' : 'Cadastrar downsell' }}
                </Button>
              </div>
            </div>

            <!-- Downsells Table -->
            <div class="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/50">
              <Table>
                <TableHeader class="bg-zinc-900/50">
                  <TableRow>
                    <TableHead class="text-[10px] uppercase font-bold text-zinc-500">Descrição</TableHead>
                    <TableHead class="text-[10px] uppercase font-bold text-zinc-500">Intervalo</TableHead>
                    <TableHead class="text-[10px] uppercase font-bold text-zinc-500">Status</TableHead>
                    <TableHead class="text-[10px] uppercase font-bold text-zinc-500 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <template v-if="editingPlan?.downsells && editingPlan.downsells.length > 0">
                    <TableRow v-for="ds in editingPlan.downsells" :key="ds.id" class="hover:bg-zinc-900/30 transition-colors">
                      <TableCell class="text-xs font-medium text-white">{{ ds.description }}</TableCell>
                      <TableCell class="text-xs text-zinc-400 font-mono">{{ intervalToHHMM(ds.sendingInterval) }}</TableCell>
                      <TableCell>
                        <Badge variant="outline" :class="ds.status === 'active' ? 'border-emerald-500/20 text-emerald-400 bg-emerald-500/5' : 'border-zinc-800 text-zinc-500'">
                          {{ ds.status === 'active' ? 'Ativo' : 'Inativo' }}
                        </Badge>
                      </TableCell>
                      <TableCell class="text-right">
                        <div class="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" class="size-8 text-zinc-500 hover:text-white hover:bg-zinc-800" @click="editDownsell(ds)">
                            <PencilIcon class="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" class="size-8 text-zinc-500 hover:text-red-400 hover:bg-red-400/10" @click="removeDownsell(ds.id)">
                            <Trash class="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  </template>
                  <TableRow v-else>
                    <TableCell colspan="4" class="h-32 text-center">
                      <div class="flex flex-col items-center gap-2">
                        <ArrowDown class="size-6 text-zinc-700" />
                        <span class="text-xs text-zinc-500">Nenhuma mensagem de downsell configurada.</span>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      <Loader2 v-else class="w-10 h-10 animate-spin text-white mx-auto" />
      <DialogFooter class="-full">
        <Button variant="ghost" class="text-white" @click="closePlanDialog">Cancelar</Button>
        <Button :disabled="isSaving || (!planForm.name || !planForm.type || !planForm.amount || !planForm.status || (upsell && !planForm.upsellMessage) || (!planForm.cycle && planForm.type !== PaymentPlanTypeEnum.LIFETIME))" @click="savePlan">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingPlan ? 'Salvar alterações' : 'Criar plano' }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>

    <!-- Delete Downsell Confirmation Dialog -->
    <DeleteConfirmationDialog
      v-model:open="isDeleteDownsellDialogOpen"
      :loading="isDeletingDownsell"
      title="Remover Downsell"
      description="Tem certeza que deseja remover esta mensagem de downsell? Esta ação não pode ser desfeita."
      @confirm="confirmDeleteDownsell"
    />
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import {
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  CreditCardIcon,
  PencilIcon,
  // CopyIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { CreatePaymentPlanDTO, GetAllPaymentPlansParams, UpdatePaymentPlanDTO, usePaymentPlan } from '@/composables/usePaymentPlan'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { PaymentPlan } from '@/interfaces/PaymentPlan'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { PaymentPlanTypeEnum, PaymentPlanTypeLabels } from '@/enums/payment-plans/PaymentPlanTypeEnum'
import { useToast } from '@/components/ui/toast'
import { ArrowDown, ArrowUp, CircleHelp, DollarSign, HelpCircle, Info, Loader2, PlusIcon } from 'lucide-vue-next'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import { PaymentPlanStatusEnum, PaymentPlanStatusLabels } from '@/enums/payment-plans/PaymentPlanStatusEnum'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Textarea } from '@/components/ui/textarea'
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxPortal, ComboboxRoot } from 'radix-vue'
import { CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { TagsInput, TagsInputInput, TagsInputItem, TagsInputItemDelete, TagsInputItemText } from '@/components/ui/tags-input'
import { useButton } from '@/composables/useButton'
import { Button as IButton } from '@/interfaces/Button'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useVariant } from '@/composables/useVariant'
import { Variant } from '@/interfaces/products/variants/Variant'
import { Product } from '@/interfaces/products/Product'
import { useProduct } from '@/composables/useProduct'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import StatusBadge from '@/components/ui/badge/StatusBadge.vue'
import { IntegrationEnum } from '@/enums/products/integrations/IntegrationEnum'
import { Badge } from '@/components/ui/badge'
import { MessageRecipientTypeEnum } from '@/enums/messages/MessageRecipientTypeEnum'
import { useMessage } from '@/composables/useMessage'
import { useTime } from '@/composables/useTime'
import { Trash } from 'lucide-vue-next'
import AnyFileUpload from '@/components/ui/upload/AnyFileUpload.vue'
import TimePicker from '@/components/ui/time/TimePicker.vue'
import RichTextarea from '@/components/ui/textarea/RichTextarea.vue'
import { useVideoCall } from '@/composables/useVideoCall'
import { Phone } from 'lucide-vue-next'

// Table Configuration
const tableHeaders = [
  { key: 'buttonMessage', label: 'Mensagem do Botão', sortable: false },
  { key: 'value', label: 'Valor/Tipo', sortable: false },
  { key: 'bindings', label: 'Vinculos', sortable: false },
  { key: 'downsells', label: 'Downsells', sortable: false },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'actions', label: '', class: 'w-32' },
]

const planTab = ref('general')

// Filters
const searchQuery = ref('')
const typeFilter = ref('all')

const { getById, getAll, create, update, destroy } = usePaymentPlan()
const isSaving = ref(false)

// Sample data (empty for demo)
const paymentPlans = ref<IPagination<PaymentPlan>>()
const { toast } = useToast()

// Dialog and Form
const isPlanDialogOpen = ref(false)
const isPlanDialogLoading = ref(false)
const editingPlan = ref<PaymentPlan | null>(null)
const deletingPLan = ref<number>()
const isDeletingPlan = ref(false)
const planForm = ref<Partial<CreatePaymentPlanDTO & UpdatePaymentPlanDTO & { videoCallIds: number[], groupIds: number[] }>>({ cycle: 1, upsellPaymentPlanIds: [], upsellButtonIds: [], videoCallIds: [], groupIds: [] })
const isDeletePlanDialogOpen = ref(false)
const perPage = ref('25')
const upsell = ref(false)

// Downsell Logic
const { create: createMessage, destroy: destroyMessage, update: updateMessage } = useMessage()
const { intervalToHHMM, convertIntervalToSeconds } = useTime()
const isAddingDownsell = ref(false)
const isSavingDownsell = ref(false)
const editingDownsellId = ref<number | null>(null)
const downsellForm = ref<Partial<any>>({
  description: '',
  message: '',
  recipientType: MessageRecipientTypeEnum.DOWNSELL,
  abandonedChat: false,
  sendingInterval: 3600, // 1 hour default
  paymentPlanIds: [],
  buttonIds: [],
  downsellPaymentPlanIds: []
})

const isDeleteDownsellDialogOpen = ref(false)
const deletingDownsellId = ref<number | null>(null)
const isDeletingDownsell = ref(false)
const downsellIntervalTime = ref('01:00')

// Downsell Multi-selects
const downsellPaymentPlanNames = ref<string[]>([])
const searchDownsellPaymentPlan = ref('')
const isDownsellPaymentPlanComboboxOpen = ref(false)
const downsellButtonNames = ref<string[]>([])
const searchDownsellButton = ref('')
const isDownsellButtonComboboxOpen = ref(false)

// Downsell Media
const downsellStartMedia = ref<File | null>(null)
const downsellStartMediaPreview = ref<string | null>(null)

const startMediaInputDownsell = ref<HTMLInputElement | null>(null)

const handleDownsellMediaChange = (event: Event, type: 'start') => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    downsellStartMedia.value = file
    downsellStartMediaPreview.value = URL.createObjectURL(file)
  }
}

watch(() => downsellIntervalTime.value, (val) => {
  const [hours, minutes] = val.split(':').map(Number)
  downsellForm.value.sendingInterval = (hours * 3600) + (minutes * 60)
})

const openNewDownsellForm = () => {
  isAddingDownsell.value = true
  downsellForm.value = {
    description: '',
    message: '',
    recipientType: MessageRecipientTypeEnum.DOWNSELL,
    abandonedChat: false,
    sendingInterval: 3600,
    paymentPlanIds: [],
    buttonIds: [],
    downsellPaymentPlanIds: [String(editingPlan.value?.id)],
    hasSpoiler: false
  }
  downsellIntervalTime.value = '01:00'
  downsellPaymentPlanNames.value = [editingPlan.value?.name ?? '']
  downsellButtonNames.value = []
  paymentPlanNamesInDownsell.value = []
}

const saveDownsell = async () => {
  if (!editingPlan.value) return
  isSavingDownsell.value = true
  
  const payload: any = {
    ...downsellForm.value,
    status: 'active',
    sendAt: new Date(),
  }

  if (downsellStartMedia.value) payload.startMedia = downsellStartMedia.value
  
  try {
    if (editingDownsellId.value) {
      // Re-map IDs for update
      payload.paymentPlanIds = downsellForm.value.paymentPlanIds
      payload.buttonIds = downsellForm.value.buttonIds
      delete payload.recipientType
      await updateMessage(editingDownsellId.value, payload)
      toast({ title: 'Downsell atualizado com sucesso', class: 'bg-green-500 text-white' })
    } else {
      await createMessage(payload)
      toast({ title: 'Downsell criado com sucesso', class: 'bg-green-500 text-white' })
    }
    
    isSavingDownsell.value = false
    cancelDownsellEdit()
    const data = await getById(editingPlan.value!.id)
    editingPlan.value = data
  } catch (error) {
    isSavingDownsell.value = false
  }
}

const cancelDownsellEdit = () => {
  isAddingDownsell.value = false
  editingDownsellId.value = null
  downsellForm.value = {
    description: '',
    message: '',
    recipientType: MessageRecipientTypeEnum.DOWNSELL,
    abandonedChat: false,
    sendingInterval: 3600,
    paymentPlanIds: [],
    buttonIds: [],
    downsellPaymentPlanIds: [],
    hasSpoiler: false
  }
  downsellIntervalTime.value = '01:00'
  downsellPaymentPlanNames.value = []
  downsellButtonNames.value = []
  downsellStartMedia.value = null
  downsellStartMediaPreview.value = null
}

const editDownsell = (ds: any) => {
  editingDownsellId.value = ds.id
  isAddingDownsell.value = true
  downsellForm.value = {
    description: ds.description,
    message: ds.message,
    recipientType: ds.recipientType,
    abandonedChat: ds.abandonedChat,
    sendingInterval: ds.sendingInterval ? convertIntervalToSeconds(ds.sendingInterval) : 0,
    paymentPlanIds: (ds.paymentPlans ?? []).map((p: any) => String(p.id)),
    buttonIds: (ds.buttons ?? []).map((b: any) => String(b.id)),
    downsellPaymentPlanIds: (ds.downsellPaymentPlans ?? []).map((p: any) => String(p.id))
  }
  downsellIntervalTime.value = intervalToHHMM(ds.sendingInterval) || '01:00'
  downsellPaymentPlanNames.value = (ds.downsellPaymentPlans ?? []).map((p: any) => p.name)
  downsellButtonNames.value = (ds.buttons ?? []).map((b: any) => b.name)
  // These names are for the "Plans" selection (upsell/offers)
  paymentPlanNamesInDownsell.value = (ds.paymentPlans ?? []).map((p: any) => p.name)
  
  // Previews
  downsellStartMediaPreview.value = ds.media?.find((m: MessageMedia) => m.placement === 'start')?.source || ds.media?.find((m: MessageMedia) => m.placement === 'top')?.source || null
}

const removeDownsell = async (id: number) => {
  deletingDownsellId.value = id
  isDeleteDownsellDialogOpen.value = true
}

const confirmDeleteDownsell = async () => {
  if (!deletingDownsellId.value) return
  
  try {
    isDeletingDownsell.value = true
    await destroyMessage(deletingDownsellId.value)
    toast({ title: 'Downsell removido', class: 'bg-green-500 text-white' })
    if (editingPlan.value) {
      const data = await getById(editingPlan.value.id)
      editingPlan.value = data
    }
    isDeleteDownsellDialogOpen.value = false
    deletingDownsellId.value = null
  } catch (error) {
    // console.log(error)
  } finally {
    isDeletingDownsell.value = false
  }
}

const searchPaymentPlan = ref('')
const { getAll: getAllButtons } = useButton()
const buttons = ref<IPagination<IButton>>()
const isButtonComboboxOpen = ref(false)
const filteredButtons = computed(() => buttons.value?.data.filter(i => !planForm.value.upsellButtonIds?.includes(i.id)))
const searchButton = ref('')
const buttonNames = ref<string[]>([])
const paymentPlanNamesInDownsell = ref<string[]>([])

const filteredPaymentPlans = computed(() => paymentPlans.value?.data.filter(i => !planForm.value.upsellPaymentPlanIds?.includes(i.id) && i.status !== PaymentPlanStatusEnum.INACTIVE))

const filteredDownsellButtons = computed(() => buttons.value?.data.filter(i => !downsellForm.value.buttonIds?.includes(String(i.id))))

const filteredDownsellPaymentPlans = computed(() => {
  return paymentPlans.value?.data.filter(i => 
    !downsellForm.value.downsellPaymentPlanIds?.includes(String(i.id)) && 
    i.status !== PaymentPlanStatusEnum.INACTIVE
  )
})

const filteredPlansForDownsellBody = computed(() => {
  return paymentPlans.value?.data.filter(i => 
    !downsellForm.value.paymentPlanIds?.includes(String(i.id)) && 
    i.status !== PaymentPlanStatusEnum.INACTIVE
  )
})

const products = ref<IPagination<Product>>()
const variants = ref<Variant[]>()
const { getAll: getAllProducts } = useProduct()
const { getAllByProductId: getAllVariantsByProductId } = useVariant()
const isVariantsLoading = ref(false)

const changeProductId = async (_productId: number) => {
  isVariantsLoading.value = true
  planForm.value.productId = _productId
  variants.value = await getAllVariantsByProductId(_productId)
  isVariantsLoading.value = false
}

const createProduct = () => {
  if (window.top) window.top.location.href = 'https://dashboard.hoopay.com.br/products/products_new'
}

// Order Bumps Logic
import { useOrderBump } from '@/composables/useOrderBump'
import { OrderBump } from '@/interfaces/OrderBump'
import { MessageMedia } from '@/interfaces/messages/MessageMedia'

const { getAll: getAllOrderBumps } = useOrderBump()
const orderBumps = ref<OrderBump[]>([])
const filteredOrderBumps = computed(() => orderBumps.value?.filter(i => !planForm.value.orderBumpIds?.includes(i.id)))
const searchOrderBump = ref('')
const isOrderBumpComboboxOpen = ref(false)
const orderBumpNames = ref<string[]>([])

const isPaymentPlanComboboxOpen = ref(false)
const paymentPlanNames = ref<string[]>([])

const loadOrderBumps = async () => {
    orderBumps.value = await getAllOrderBumps()
}

// Video Call Logic
const { fetchBotVideoCalls } = useVideoCall()
const videoCalls = ref<IVideoCall[]>([])
const filteredVideoCalls = computed(() => videoCalls.value?.filter(i => !planForm.value.videoCallIds?.includes(i.id)))
const searchVideoCall = ref('')
const isVideoCallComboboxOpen = ref(false)
const videoCallNames = ref<string[]>([])

const loadVideoCalls = async () => {
    if (editingPlan.value?.botId) {
        videoCalls.value = await fetchBotVideoCalls(editingPlan.value.botId)
    } else {
        // Find botId from paymentPlans if possible, or use one from current filters
        // For simplicity, we can fetch all or handle it in openDialog
    }
}


const createVariant = () => {
  if (window.top) window.top.location.href = 'https://dashboard.hoopay.com.br/products/edit/' + planForm.value.productId
}

// Group Logic
import { useGroup } from '@/composables/useGroup'
import { Group } from '@/interfaces/Group'

const { getAll: getAllGroups } = useGroup()
const groups = ref<Group[]>([])
const filteredGroups = computed(() => groups.value?.filter(i => !planForm.value.groupIds?.includes(i.id)))
const searchGroup = ref('')
const isGroupComboboxOpen = ref(false)
const groupNames = ref<string[]>([])

const loadGroups = async () => {
    const data = await getAllGroups({ perPage: '100' })
    if ('data' in data) {
      groups.value = data.data
    } else {
      groups.value = data
    }
}

// Actions
const openNewPlanDialog = () => {
  planForm.value.productId = undefined
  variants.value = undefined
  upsell.value = false
  paymentPlanNames.value = []
  buttonNames.value = []
  orderBumpNames.value = []
  videoCallNames.value = []
  groupNames.value = []
  editingPlan.value = null
  planForm.value = { cycle: 1, upsellPaymentPlanIds: [], upsellButtonIds: [], orderBumpIds: [], videoCallIds: [], groupIds: [], status: PaymentPlanStatusEnum.ACTIVE, buttonStyle: undefined }
  isPlanDialogOpen.value = true
  isPaymentPlanComboboxOpen.value = false
  isButtonComboboxOpen.value = false
  if (orderBumps.value.length === 0) loadOrderBumps()
  if (groups.value.length === 0) loadGroups()
}

const closePlanDialog = () => {
  isPlanDialogOpen.value = false
}

const editPlan = async (plan: PaymentPlan) => {
  isPlanDialogOpen.value = true
  isPlanDialogLoading.value = true
  editingPlan.value = await getById(plan.id)
  
  if (orderBumps.value.length === 0) await loadOrderBumps()

  planForm.value = {
    name: plan.name,
    type:  plan.type,
    cycle: plan.cycle,
    amount: plan.amount,
    paymentApprovedMessage: plan.paymentApprovedMessage,
    buttonMessage:  plan.buttonMessage,
    upsellMessage: plan.upsellMessage,
    status: plan.status,
    upsellPaymentPlanIds: (editingPlan.value?.upsellPaymentPlans ?? []).map(p => p.id),
    upsellButtonIds: (editingPlan.value?.upsellButtons ?? []).map(b => b.id),
    orderBumpIds: (editingPlan.value?.orderBumps ?? []).map(b => b.id),
    videoCallIds: (editingPlan.value?.videoCalls ?? []).map(vc => vc.id),
    groupIds: (editingPlan.value?.groups ?? []).map(g => g.id),
    checkoutButtonLabel: plan.checkoutButtonLabel,
    productId: plan.productId,
    variantUUID: plan.variantUUID,
    buttonStyle: plan.buttonStyle
  }
  paymentPlanNames.value = editingPlan.value.upsellPaymentPlans?.map(p => p.name) ?? []
  buttonNames.value = editingPlan.value.upsellButtons?.map(b => b.name) ?? []
  orderBumpNames.value = editingPlan.value.orderBumps?.map(b => b.name) ?? []
  videoCallNames.value = editingPlan.value.videoCalls?.map(vc => vc.name) ?? []
  groupNames.value = editingPlan.value.groups?.map(g => g.name) ?? []
  
  upsell.value = !!editingPlan.value.upsellMessage
  isPlanDialogLoading.value = false
  isPlanDialogOpen.value = true
  isPaymentPlanComboboxOpen.value = false
  isButtonComboboxOpen.value = false

  if (videoCalls.value.length === 0) loadVideoCalls()
  if (groups.value.length === 0) loadGroups()
  if (plan.productId) {
    variants.value = await getAllVariantsByProductId(plan.productId) 
  }
}

const deletePlan = (plan: PaymentPlan) => {
  deletingPLan.value = plan.id
  isDeletePlanDialogOpen.value = true
}

const savePlan = async () => {
  isSaving.value = true
  let payload: any = { ...planForm.value }

  if (!payload.buttonMessage) {
    payload.buttonMessage = payload.name
  }

  if (payload.type === PaymentPlanTypeEnum.LIFETIME) {
    delete payload.cycle
  }

  if (!upsell.value) {
    payload.upsellMessage = null
    payload.upsellPaymentPlanIds = []
    payload.upsellButtonIds = []
  }

  if (!editingPlan.value) {
    create(payload as CreatePaymentPlanDTO, () => {
      isSaving.value = false
      toast({
        title: 'Plano de Pagamento criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      getAll({ withGroupsAndPackages: true, withDownsells: true, withVideoCalls: true }).then((data) => paymentPlans.value = data)
      closePlanDialog()
    })
  } else {
    try {
      await update(editingPlan.value.id, payload)
      isSaving.value = false
        toast({
          title: 'Plano de Pagamento editado com sucesso',
          class: 'bg-green-500 text-white',
          duration: 1500
        })
        getAll({ withGroupsAndPackages: true, withDownsells: true, withVideoCalls: true }).then((data) => paymentPlans.value = data)
        closePlanDialog()
    } catch (error) {
      isSaving.value = false
    }
  }
}

const sortBy = (key: string) => {
  // Implement sorting logic
}

const cycleLabel = computed(() => {
    if (planForm.value.type === PaymentPlanTypeEnum.LIFETIME) return 'Vitalício';
    
    const cycleValue = planForm.value.cycle ?? 1;
    const units = {
        daily: cycleValue > 1 ? 'dias' : 'dia',
        weekly: cycleValue > 1 ? 'semanas' : 'semana',
        monthly: cycleValue > 1 ? 'meses' : 'mês',
        yearly: cycleValue > 1 ? 'anos' : 'ano'
    };
    
    return `${cycleValue} ${units[planForm.value.type as keyof typeof units] || ''}`;
})

const reloadAndGetAll = (params: GetAllPaymentPlansParams) => {
  paymentPlans.value = undefined
  getAll({ ...params, withDownsells: true, withVideoCalls: true }).then((data) => paymentPlans.value = data)
}

watch([() => typeFilter.value, () => perPage.value], () => {
  reloadAndGetAll({ type: typeFilter.value === 'all' ? undefined : typeFilter.value as PaymentPlanTypeEnum, search: searchQuery.value, perPage: perPage.value, withGroupsAndPackages: true, withDownsells: true, withVideoCalls: true  })
})

watch(() => isPlanDialogOpen.value, () => {
  if (!buttons.value) {
    getAllButtons()
      .then((data) => buttons.value = data)
    getAllProducts({ integrations: [IntegrationEnum.BOTEXPRESS], perPage: '100' })
      .then((data) => products.value = data)
  }
})

watch(() => planTab.value, () => {
  if (planTab.value === 'downsell' && editingPlan.value?.id) {
    const paymentPlan = paymentPlans.value?.data.find(plan => plan.id == editingPlan.value?.id)
    if ((paymentPlan?.groups?.length ?? 0) === 0 && (paymentPlan?.packages?.length ?? 0) === 0) {
      planTab.value = 'general'
      toast({
        title: 'Vincule pelo menos um grupo ou pack ao plano de pagamento antes de configurar o downsell.',
        class: 'bg-red-500 text-white',
        duration: 2000
      })
    }
  }
})

onMounted(() => {
  getAll({ withGroupsAndPackages: true, withDownsells: true, withVideoCalls: true, perPage: perPage.value }).then((data) => paymentPlans.value = data)
})
</script>